import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { submit } from 'redux-form';
import {
  isParticipantClipped,
  ParticipantState,
  isUnknownParticipantName,
  SnapcodeViewModel,
  ParticipantViewModel,
  EventType,
  PortalClipVisibility,
  ParticipantActionViewModel,
  ClipPurchaseType,
  UserRole,
} from 'stageclip-common';
import { faTrash, faEdit, faFileVideo, faPlus, faMinus, faVideo } from '@fortawesome/free-solid-svg-icons';
import get from 'lodash/get';

import { createModalCloseAction, createModalOpenAction } from '../../../actions/modalActions';
import { DataRequestActionTypes } from '../../../constants/actionTypes';
import { FormId } from '../../../constants/formId';
import { SimpleModalId } from '../../../constants/modalId';
import { ClippingPlatformState } from '../../../store/clippingPlatformState';
import { baseMapDispatchToProps, ComponentBase, BaseDispatchProps, BaseStateProps } from '../../componentBase';
import AppForm from '../../form/appForm';
import AppPartialSubmitForm from '../../form/appPartialSubmitForm';
import { AppSelectField } from '../../form/appSelectField';
import { AppTextField } from '../../form/appTextField';
import { integer, minValue, required } from '../../form/formValidations';
import AppModal from '../../modal/appModal';
import TabulatedForm from '../../modal/tabulatedForm';
import { mapBaseEventStateToProps } from '../baseEventForm';

import { ActionLogDetailsModal } from 'components/actionLogDetailsModal/ActionLogDetailsModal';
import { ParticipantActionsLogContainer } from './actionsLog/ParticipantActionsLogContainer';
import { getItem } from 'store/selectors';
import { createGetParticipantVgAdminLink, createGetParticipantVgLink } from 'api/participants/participants.actions';
import { callApi } from 'helpers/callApi/callApi';
import BaseParticipantFormFields from './baseParticipantFormFields';
import styles from './editParticipantForm.module.scss';
import GetDetailsFromParticipantFromFields from './mergeParticipantsFromFields';
import Button from 'components/buttons/button';
import { PreviewEmailContainer } from './previewEmail/PreviewEmailContainer';
import { NormalizedEvent } from 'models/normalized/normalizedEvent';

interface EditParticipantFormOwnProps {
  eventId: string;
  eventType?: EventType;
  isSurveyTurnedOn: boolean;
  isSnapchatTurnedOn: boolean;
  snapcodes: SnapcodeViewModel[];
  clipPurchaseType?: ClipPurchaseType;
  event?: NormalizedEvent;
  participantId?: string;
}

interface EditParticipantFormStateProps extends BaseStateProps {
  editParticipantData: { participant: ParticipantViewModel; reloadParticipants: () => void };
  isUnknownParticipant: boolean;
  isClippedParticipant: boolean;
  blacklist: boolean;
}

interface EditParticipantFormDispatchProps extends BaseDispatchProps {
  openDeleteParticipantModal: (p: ParticipantViewModel, reloadParticipants: () => void) => void;
  openBlacklistConfirmationModal: (p: ParticipantViewModel) => void;
  closeModal: () => void;
  submitForm: (formId: string) => void;
  getParticipantVgAdminLink: (participantId: string) => void;
  getParticipantVgLink: (participantId: string) => void;
  openDetails: (p: Pick<ParticipantActionViewModel, 'data' | 'action' | 'timestamp'>) => void;
  openUnlockClipModal: (data: { event: NormalizedEvent; participantId: string }) => void;
  openResetRenderModal: (data: { event: NormalizedEvent; participantId: string }) => void;
}

type EditParticipantFormState = { formId: string };

// this is needed to hack TabulatedForm and display action logs
const TabWrapper = ({ children }: any) => children;

class EditParticipantForm extends ComponentBase<
  EditParticipantFormOwnProps,
  EditParticipantFormStateProps,
  EditParticipantFormDispatchProps,
  EditParticipantFormState
> {
  private static getParticipantStateOptions() {
    return [
      { value: ParticipantState.New, label: 'New' },
      { value: ParticipantState.Processed, label: 'Processed' },
      { value: ParticipantState.ReadyToGenerateClip, label: 'Ready for the Clip Generator' },
      { value: ParticipantState.Error, label: 'Error' },
    ];
  }

  getParticipantClipVisibilityOptions = () => {
    return [
      { value: PortalClipVisibility.Public, label: 'Public' },
      { value: PortalClipVisibility.Unlisted, label: 'Unlisted' },
      { value: PortalClipVisibility.Blacklisted, label: 'Blacklisted' },
    ];
  };

  constructor(props: any) {
    super(props);
    this.state = {
      formId: FormId.EditParticipant,
    };
  }

  protected loggedInRender() {
    const { formId } = this.state;
    const {
      editParticipantData,
      eventId,
      isUnknownParticipant,
      isClippedParticipant,
      isSurveyTurnedOn,
      isSnapchatTurnedOn,
      snapcodes,
      openDeleteParticipantModal,
      closeModal,
      getParticipantVgAdminLink,
      getParticipantVgLink,
      eventType,
      openBlacklistConfirmationModal,
      blacklist,
      openUnlockClipModal,
      openResetRenderModal,
      event,
    } = this.props;

    if (editParticipantData.participant?.fullName) {
      const isParticipantUnknown = isUnknownParticipantName(editParticipantData.participant?.fullName);
      const selectedForm = isParticipantUnknown && FormId.GetDetailsFromParticipant;

      if (isParticipantUnknown) {
        this.setState({
          formId: selectedForm,
        });
      }
    }

    const hideFormButtons = ['email-preview', 'action-logs'].includes(formId);
    // an assigned clip of a live event is sent to render straight away, so assigning needs a finalized template
    const isTemplateNotFinalized =
      [EventType.LiveEvent, EventType.HybridClipsOnlyEvent, EventType.HybridFullCeremonyEvent].includes(eventType) &&
      !event?.eventConfiguration?.isLiveTemplateFinalize;

    return (
      <>
        <AppModal
          id={SimpleModalId.EditParticipant}
          submitAction={hideFormButtons ? undefined : this.submitForm}
          title="Edit participant"
          hideOpenButton
          hideActionButtons={hideFormButtons || this.props.currentUser?.role === UserRole.LimitedClipper}
          submitDisabled={formId === FormId.GetDetailsFromParticipant && isTemplateNotFinalized}
        >
          <TabulatedForm handleSelectedFormChange={this.handleFormChange}>
            {isUnknownParticipant && (
              <AppForm
                form={FormId.GetDetailsFromParticipant}
                title="Get details from participant"
                submitActionType={DataRequestActionTypes.MergeParticipantsRequested}
                reduxData={{ editParticipantData, eventId }}
                hideButtons
                onSuccess={this.onSuccess}
              >
                <GetDetailsFromParticipantFromFields
                  eventId={eventId}
                  isTemplateNotFinalized={isTemplateNotFinalized}
                />
              </AppForm>
            )}
            <AppPartialSubmitForm
              form={FormId.EditParticipant}
              title="Edit Participant"
              submitActionType={DataRequestActionTypes.EditParticipantRequested}
              reduxData={{ ...editParticipantData.participant, eventId }}
              initialValues={editParticipantData.participant}
              hideButtons
              onSuccess={this.onSuccess}
            >
              <BaseParticipantFormFields
                readonly={isUnknownParticipant}
                isSurveyTurnedOn={isSurveyTurnedOn}
                isSnapchatTurnedOn={isSnapchatTurnedOn}
                snapcodes={snapcodes}
              />
              <AppSelectField
                name="portalClipVisibility"
                label="Clip visibility"
                options={this.getParticipantClipVisibilityOptions()}
              />
              <AppTextField name="eventOrderIndex" label="Order Index" validate={[required, minValue(1), integer]} />
              <AppSelectField
                name="state"
                label="State"
                options={EditParticipantForm.getParticipantStateOptions()}
                validate={[required]}
              />
              <AppTextField name="eventVideo.name" label="Event video" readonly />
              <AppTextField name="clipStartTimestamp" label="Video start time" />
              <AppTextField name="clipEndTimestamp" label="Video end time" />
              <AppTextField name="audioStartTimestamp" label="Audio start time" />
              <AppTextField name="audioEndTimestamp" label="Audio end time" />
              <AppTextField name="wooCommerceId" label="Woo Commerce ID" />
              <AppTextField name="wooCommercePermalink" label="Woo Commerce Permalink" />
              <AppTextField name="clipVideoUri" label="Clip Video URI" />
              <AppTextField name="clipThumbnailUri" label="Clip Thumbnail URI" />
              <AppTextField name="snapcodeId" label="Snapcode Id" readonly />
              <AppTextField name="virtualGraduation.image" label="Slide Image" readonly />
              <AppTextField name="virtualGraduation.message" label="Slide Message" readonly />
              <AppTextField name="virtualGraduation.slidePath" label="MO Slide" readonly />
              <AppTextField name="virtualGraduation.audioPath" label="MO Audio" readonly />

              <div className={styles.buttons}>
                {((!!eventType && eventType === EventType.VirtualGraduation) ||
                  eventType === EventType.HybridClipsOnlyEvent ||
                  eventType === EventType.HybridFullCeremonyEvent) && (
                  <>
                    <Button
                      type="button"
                      onClick={() => openBlacklistConfirmationModal(editParticipantData.participant)}
                      text={blacklist ? 'Remove from Blacklist' : 'Add to Blacklist'}
                      allCapitals
                      icon={blacklist ? faMinus : faPlus}
                      inverseColours
                    />
                    <Button
                      type="button"
                      onClick={() => {
                        getParticipantVgLink(editParticipantData.participant?.id);
                      }}
                      text="Upload Page"
                      allCapitals
                      icon={faFileVideo}
                    />
                    <Button
                      type="button"
                      onClick={() => {
                        getParticipantVgAdminLink(editParticipantData.participant?.id);
                      }}
                      text="Admin Upload Page"
                      allCapitals
                      icon={faEdit}
                    />
                  </>
                )}
                <Button
                  type="button"
                  className={styles.delete}
                  onClick={() => {
                    closeModal();
                    openDeleteParticipantModal(
                      editParticipantData.participant,
                      this.props.editParticipantData.reloadParticipants,
                    );
                  }}
                  text="Delete"
                  allCapitals
                  icon={faTrash}
                  disabled={this.props.currentUser?.role === UserRole.LimitedClipper}
                />
                {event.isAllowToPurchaseClip && editParticipantData.participant?.participantPaymentState === null && (
                  <Button
                    type="button"
                    className={styles.delete}
                    onClick={() => {
                      closeModal();
                      openUnlockClipModal({ event, participantId: editParticipantData.participant?.id });
                    }}
                    text="Unlock Clip"
                    allCapitals
                    icon={faVideo}
                    disabled={this.props.currentUser?.role === UserRole.LimitedClipper}
                  />
                )}
                {event.isSelfClipping && (
                  <Button
                    type="button"
                    className={styles.delete}
                    onClick={() => {
                      closeModal();
                      openResetRenderModal({ event, participantId: editParticipantData.participant?.id });
                    }}
                    text="Reset Render"
                    allCapitals
                    icon={faVideo}
                    disabled={this.props.currentUser?.role === UserRole.LimitedClipper}
                  />
                )}
              </div>
            </AppPartialSubmitForm>
            {!isUnknownParticipant && isClippedParticipant && (
              <AppForm
                form={FormId.ConvertToUnknownParticipant}
                title="Convert to unknown participant"
                submitActionType={DataRequestActionTypes.ConvertToUnknownParticipantRequested}
                reduxData={{ eventId, id: editParticipantData.participant?.id }}
                hideButtons
                onSuccess={this.onSuccess}
              >
                <div className={styles.convertToUnknownParticipantInfo}>
                  This action splits the data on this participant.
                  <br />
                  One participant containing participant details (fullName, subtitle and clientParticipantId) <br />
                  and an unknown participant containing the clipping data.
                </div>
              </AppForm>
            )}
            <TabWrapper title="Audit log" form="action-logs">
              <ParticipantActionsLogContainer
                params={editParticipantData.participant?.id}
                openDetails={this.props.openDetails}
              />
            </TabWrapper>
            <TabWrapper title="Email preview" form="email-preview">
              <PreviewEmailContainer
                participantId={editParticipantData.participant?.id}
                eventId={editParticipantData.participant?.eventId}
              />
            </TabWrapper>
          </TabulatedForm>
        </AppModal>
        <ActionLogDetailsModal />
      </>
    );
  }

  private handleFormChange = (formId: string) => {
    this.setState({ formId });
  };

  private onSuccess = () => {
    this.handleFormChange(FormId.EditParticipant);
    this.props.editParticipantData.reloadParticipants();
    this.props.closeModal();
  };

  private submitForm = () => {
    this.props.submitForm(this.state.formId);
  };
}

function mapStateToProps(state: ClippingPlatformState): EditParticipantFormStateProps {
  const modalData = state.appStatus.modals[SimpleModalId.EditParticipant];
  const participantId = modalData?.props?.participant?.id;

  return {
    ...mapBaseEventStateToProps(state),
    editParticipantData: modalData ? modalData.props : {},
    isClippedParticipant: modalData?.props?.participant && isParticipantClipped(modalData.props.participant),
    isUnknownParticipant:
      modalData?.props?.participant?.fullName && isUnknownParticipantName(modalData.props.participant.fullName),
    blacklist: participantId ? get(getItem(state.entities.participants, participantId), 'blacklist') : false,
  };
}

function mapDispatchToProps(dispatch: Dispatch): EditParticipantFormDispatchProps {
  return {
    ...baseMapDispatchToProps(dispatch),
    openDeleteParticipantModal: ({ id, fullName, eventId }, reloadParticipants) =>
      dispatch(
        createModalOpenAction(SimpleModalId.DeleteParticipant, {
          participantId: id,
          participantName: fullName,
          eventId,
          reloadParticipants,
        }),
      ),
    openBlacklistConfirmationModal: ({ id, eventId }) =>
      dispatch(
        createModalOpenAction(SimpleModalId.ParticipantBlacklistConfirmation, {
          participantId: id,
          eventId,
        }),
      ),
    closeModal: () => dispatch(createModalCloseAction(SimpleModalId.EditParticipant)),
    submitForm: (formId: string) => dispatch(submit(formId)),
    getParticipantVgLink: participantId =>
      // @ts-ignore
      callApi({ action: () => dispatch(createGetParticipantVgLink(participantId)) }),
    getParticipantVgAdminLink: participantId =>
      // @ts-ignore
      callApi({ action: () => dispatch(createGetParticipantVgAdminLink(participantId)) }),
    openDetails: data => dispatch(createModalOpenAction(SimpleModalId.LogDetails, data)),
    openUnlockClipModal: ({ event, participantId }) =>
      dispatch(
        createModalOpenAction(SimpleModalId.UnlockClip, {
          event,
          participantId,
        }),
      ),
    openResetRenderModal: ({ event, participantId }) =>
      dispatch(
        createModalOpenAction(SimpleModalId.ResetRender, {
          event,
          participantId,
        }),
      ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(EditParticipantForm);
